import * as api from '@/utils/workManagementApi'
import { isWithdrawnWorkResult } from '@/policies/work-result-status-policy.mjs'
import { startAsyncRpaPanelTracking } from '@/policies/rpa-async-panel-tracking-policy.mjs'
import {
  buildWorkTemplateBasePayload,
  buildWorkTemplateUpdatePayload,
} from '@/views/WorkManagement/work-template-schedule-policy.mjs'
import {
  buildWorkItemAssignments,
  buildChangedWorkItemAssignments,
  normalizeAssignmentSources,
  assignmentSourcesForEdit,
  assignmentSourceDisplayRows,
  splitWorkItemAssignments,
} from '@/views/WorkManagement/work-assignment-policy.mjs'
import { mergeRpaInputFieldsWithFlowBlocks } from '@/views/WorkManagement/work-rpa-form-input-policy.mjs'
import { rpaInputBlockName } from '@/views/WorkManagement/work-rpa-input-display-policy.mjs'
import { resolveCurrentWorkItemAttemptId } from '@/views/WorkManagement/work-attempt-policy.mjs'
import { workItemFlowPath } from '@/views/WorkManagement/work-flow-detail-policy.mjs'
import {
  isFailedWorkRun,
  isReviewableWorkRun,
  normalizeWorkReviewTargets,
  workManagementFirstDemoErrorMessage,
  workRunDisplayOutputs,
  workRunFailure,
  workRunFailureMessage,
} from '@/views/WorkManagement/work-management-first-demo-policy.mjs'
import {
  buildTemplateGraphPayload,
  normalizeTemplateGraph,
  templateGraphCanvasFlows,
  templateGraphCanvasItems,
} from '@/views/WorkManagement/work-template-runtime-graph-policy.mjs'
import { normalizePermissionOrganization } from '@/views/WorkManagement/work-template-group-permission-policy.mjs'
import { normalizeWorkFlowAccess } from '@/views/WorkManagement/work-flow-access-policy.mjs'
import { normalizeWorkEventListPayload } from '@/views/WorkManagement/work-event-log-policy.mjs'
import { assignmentHistoryParams } from '@/views/WorkManagement/work-assignment-history-policy.mjs'

const STATUS_TEXT = {
  draft: '초안',
  active: '진행 중',
  inactive: '비활성',
  archived: '보관',
  scheduled: '예약됨',
  reopened: '다시 열림',
  canceled: '취소',
  blocked: '대기',
  pending: '할당됨',
  in_progress: '진행 중',
  reviewing: '검토 중',
  completed: '완료',
  rejected: '반려',
  revision_requested: '수정 요청',
  running: '실행 중',
  success: '성공',
  failed: '실패',
  candidate: '검토 중',
  official: '공식',
  superseded: '대체됨',
  rolled_back: '롤백됨',
}

const ITEM_TYPE_TEXT = {
  file_input: '파일 입력',
  form_input: '폼 입력',
  rpa_run: '워크플로우 실행',
  file_review: '파일 검토',
  approval: '승인',
  manual: '수동 확인',
  manual_check: '수동 확인',
}

const RPA_INPUT_ACTIVITY_TYPES = new Set([
  'data',
  'data_ev',
  'data_form',
  'form',
  'data_rpa',
  'data_find_file',
  'find file',
])

function unwrap(response, fallback = null) {
  if (!response) return fallback
  if (Object.prototype.hasOwnProperty.call(response, 'data')) return response.data
  return response
}

function metaOf(response) {
  return response && response.meta ? response.meta : null
}

function paginationParams(params = {}, defaultLimit = 50) {
  const rest = { ...params }
  delete rest.offset
  delete rest.total
  delete rest.maxPage
  delete rest.max_page
  return {
    ...rest,
    page: Math.max(1, Number(rest.page) || 1),
    limit: Math.max(1, Number(rest.limit) || defaultLimit),
  }
}

function normalizePaginationMeta(meta = {}, fallback = {}) {
  const limit = Math.max(1, Number(meta.limit) || Number(fallback.limit) || 50)
  const total = Math.max(0, Number(meta.total) || 0)
  const page = Math.max(1, Number(meta.page) || Number(fallback.page) || 1)
  const maxPage = Math.max(1, Number(meta.max_page ?? meta.maxPage) || Math.ceil(total / limit) || 1)
  const additionalMeta = { ...(meta || {}) }
  delete additionalMeta.offset
  return {
    ...additionalMeta,
    total,
    page,
    max_page: maxPage,
    maxPage,
    limit,
  }
}

function asArray(response) {
  const data = unwrap(response, [])
  return Array.isArray(data) ? data : []
}

function normalizeAssignee(row) {
  if (!row) return null
  return {
    ...row,
    id: row.user_id || row.id,
    user_id: row.user_id || row.id,
    name: row.name || row.user_name || '',
    username: row.username || '',
    role: row.role || 'assignee',
    notify_enabled: row.notify_enabled !== false,
    required: row.required !== false,
  }
}

function normalizeAssigneeCandidate(row) {
  if (!row || row.assignable === false || !row.user_id) return null
  return {
    id: row.user_id,
    user_id: row.user_id,
    name: row.name || row.email || `User #${row.user_id}`,
    email: row.email || '',
    picture: row.picture || null,
    personal_color: row.personal_color || null,
    organization_root: row.organization_root,
    organization_root_name: row.organization_root_name || '',
    organization_sub: row.organization_sub,
    organization_sub_name: row.organization_sub_name || '',
    sources: row.sources || [],
    invited_organization_ids: row.invited_organization_ids || [],
    direct_invite: row.direct_invite === true,
    assignable: true,
  }
}

function normalizeAssigneeOrganization(row) {
  const normalized = normalizePermissionOrganization(row)
  if (!normalized) return null
  const name = normalized.name || `Group #${normalized.id}`
  return {
    ...normalized,
    id: normalized.id,
    value: normalized.id,
    text: name,
    name,
    organization_pk: normalized.id,
    organization_name: name,
    organization_root_pk: normalized.organization_root_pk || normalized.root || (normalized.is_root ? normalized.id : null),
    organization_root_name: normalized.organization_root_name !== undefined
      ? normalized.organization_root_name
      : normalized.root_name || (normalized.is_root ? name : ''),
    type: 'organization',
  }
}

function normalizeTemplateItem(item = {}, index = 0) {
  const assignees = assignmentSourcesForEdit(item)
  const fallbackAssignee = item.default_assignee_id ? normalizeAssignee({ user_id: item.default_assignee_id }) : null
  const assignmentState = splitWorkItemAssignments(assignees, fallbackAssignee)
  const inputConfig = item.input_config || {}
  const layout = item.layout || item.position || inputConfig._layout || inputConfig.layout || item.metadata?.layout || {}
  return {
    ...item,
    local_id: item.local_id || item.id || index + 1,
    item_type: item.item_type === 'manual_check' ? 'manual' : item.item_type,
    required: item.required !== false,
    completion_policy: item.completion_policy || 'any_one',
    default_assignee_id: assignmentState.assigneeId,
    assignee: assignmentState.assignee,
    assignees,
    watcher_ids: assignmentState.watcherIds,
    watchers: assignmentState.watchers,
    input_config: inputConfig,
    rpa_input_activities: (item.rpa_input_activities || []).map(normalizeRpaInputActivity),
    notification_rule: item.notification_rule || {},
    x: Number.isFinite(item.x) ? item.x : Number.isFinite(layout.x) ? layout.x : index % 4,
    y: Number.isFinite(item.y) ? item.y : Number.isFinite(layout.y) ? layout.y : Math.floor(index / 4),
    w: Number.isFinite(item.w) ? item.w : Number.isFinite(layout.w) ? layout.w : 1,
    h: Number.isFinite(item.h) ? item.h : Number.isFinite(layout.h) ? layout.h : 1,
  }
}

function normalizeTemplate(template) {
  if (!template) return null
  const hasGraph = Boolean(
    template.graph
    && Array.isArray(template.graph.nodes)
    && Array.isArray(template.graph.edges)
  )
  const graph = hasGraph ? normalizeTemplateGraph(template) : null
  const items = (graph ? templateGraphCanvasItems(graph) : template.items || []).map(normalizeTemplateItem)
  const itemIdByClientKey = new Map()
  items.forEach(item => {
    const key = item.client_key || item.key
    if (key) itemIdByClientKey.set(String(key), item.local_id || item.id)
  })
  const flowItemId = (...candidates) => {
    const found = candidates.find(value => value !== undefined && value !== null && value !== '')
    if (found === undefined) return null
    return itemIdByClientKey.get(String(found)) || found
  }
  return {
    ...template,
    project_name: template.project_name || '',
    name: template.name || '',
    created_by: template.created_by || template.creator || template.owner || { name: '-' },
    schedule_rule: template.schedule_rule || {},
    notification_rule: template.notification_rule || {},
    items,
    graph,
    flows: (graph ? templateGraphCanvasFlows(graph) : template.flows || template.edges || []).map(edge => ({
      ...edge,
      from_item_template_id: flowItemId(edge.from_item_template_id, edge.from_item_id, edge.start_item_id, edge.from_client_key),
      to_item_template_id: flowItemId(edge.to_item_template_id, edge.to_item_id, edge.end_item_id, edge.to_client_key),
      start_item_id: flowItemId(edge.start_item_id, edge.from_item_template_id, edge.from_item_id, edge.from_client_key),
      end_item_id: flowItemId(edge.end_item_id, edge.to_item_template_id, edge.to_item_id, edge.to_client_key),
      start_item_position: edge.start_item_position || 'right',
      end_item_position: edge.end_item_position || 'left',
    })),
    permissions: template.permissions || {},
  }
}

function normalizeWorkAssigneeNames(work = {}) {
  if (Array.isArray(work.assignee_sources)) {
    return assignmentSourceDisplayRows({
      assignee_sources: work.assignee_sources,
      assignees: normalizeWorkAssigneeUsers(work),
    }).map(row => row.text)
  }
  const names = []
  const pushName = value => {
    if (!value) return
    if (typeof value === 'string') {
      names.push(value)
      return
    }
    const name = value.name || value.user_name || value.email
    if (name) names.push(name)
  }
  ;(work.assignee_names || []).forEach(pushName)
  const assignees = work.assignees
  if (Array.isArray(assignees)) assignees.forEach(pushName)
  else if (assignees && Array.isArray(assignees.users)) assignees.users.forEach(pushName)
  return Array.from(new Set(names))
}

function normalizeWorkAssigneeUsers(work = {}) {
  const assignees = work.assignees
  if (!assignees || !Array.isArray(assignees.users)) return []
  return assignees.users
    .map(user => ({ id: user.id, name: user.name }))
    .filter(user => user.id != null)
}

function normalizeWork(work = {}) {
  return {
    ...work,
    flow_access: normalizeWorkFlowAccess(work),
    template_name: work.template_name || '',
    project_name: work.project_name || '',
    published_at: work.published_at || work.created_at,
    owner: work.owner || { name: '-' },
    progress: Number.isFinite(work.progress) ? work.progress : 0,
    assignee_names: normalizeWorkAssigneeNames(work),
    assignee_users: normalizeWorkAssigneeUsers(work),
  }
}

function finiteWorkItemLayout(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return Number.isFinite(value.x) && Number.isFinite(value.y) ? value : null
}

function storedWorkItemLayout(item = {}) {
  const inputConfig = item.input_config || item.metadata?.input_config || {}
  return [
    { x: item.x, y: item.y, w: item.w, h: item.h },
    item.layout,
    item.position,
    inputConfig._layout,
    inputConfig.layout,
    item.metadata?.layout,
  ].map(finiteWorkItemLayout).find(Boolean) || null
}

function templateRevisionWorkItemLayouts(revision = {}) {
  const nodes = revision?.snapshot?.graph?.nodes
  const layouts = new Map()
  if (!Array.isArray(nodes)) return layouts
  nodes.forEach(node => {
    if (node?.node_type !== 'work_item' || !node.client_key) return
    const layout = finiteWorkItemLayout(node.layout)
    if (layout) layouts.set(String(node.client_key), layout)
  })
  return layouts
}

function templateRevisionFlowGraph(revision = {}) {
  return normalizeTemplateGraph(revision?.snapshot?.graph || {})
}

function publishedWorkFlowItems(items = [], revision = {}) {
  const graphItems = templateGraphCanvasItems(templateRevisionFlowGraph(revision))
  if (!graphItems.length) return items
  const workItemsByKey = new Map(items.map(item => [String(item.client_key || item.key), item]))
  const includedWorkItemKeys = new Set()
  const publishedItems = graphItems.flatMap(graphItem => {
    const key = String(graphItem.client_key || graphItem.key)
    const workItem = workItemsByKey.get(key)
    if (graphItem.node_type === 'runtime_definition') {
      if (!workItem) return [graphItem]
      includedWorkItemKeys.add(key)
      return [{ ...graphItem, ...workItem, node_type: 'runtime_definition' }]
    }
    if (!workItem) return []
    includedWorkItemKeys.add(key)
    return [{ ...graphItem, ...workItem, node_type: 'work_item' }]
  })
  items.forEach(item => {
    const key = String(item.client_key || item.key)
    if (!includedWorkItemKeys.has(key)) publishedItems.push({ ...item, node_type: 'work_item' })
  })
  return publishedItems
}

function publishedWorkFlowEdges(flows = [], revision = {}) {
  const graph = templateRevisionFlowGraph(revision)
  const runtimeKeys = new Set(graph.nodes
    .filter(node => node.node_type === 'runtime_definition')
    .map(node => String(node.client_key)))
  if (!runtimeKeys.size) return flows
  const edgeKey = edge => `${edge.from_node_key || edge.from_client_key}:${edge.condition || 'completed'}:${edge.to_node_key || edge.to_client_key}`
  const currentKeys = new Set(flows.map(edgeKey))
  const runtimeEdges = templateGraphCanvasFlows(graph).filter(edge => (
    runtimeKeys.has(String(edge.from_node_key)) || runtimeKeys.has(String(edge.to_node_key))
  )).filter(edge => !currentKeys.has(edgeKey(edge)))
  return [...flows, ...runtimeEdges]
}

function normalizeWorkItem(item = {}, work = null, index = 0) {
  const assignees = (item.assignees || []).map(normalizeAssignee).filter(Boolean)
  const sources = assignmentSourcesForEdit(item)
  const assignmentState = splitWorkItemAssignments(sources)
  const assignee = splitWorkItemAssignments(assignees, normalizeAssignee(item.assignee)).assignee
  const inputConfig = item.input_config || item.metadata?.input_config || {}
  const layout = storedWorkItemLayout(item) || {}
  const itemWork = work || item.work || null
  const workName = item.work_name || itemWork?.name || `Work #${item.work_id || ''}`
  const itemName = item.name || ''
  const displayName = [workName, itemName].filter(Boolean).join(' - ') || item.display_name || '-'
  const reviewTargetOutputs = (item.review_target_outputs || item.metadata?.review_target_outputs || []).map(normalizeOutput)
  const reviewTargets = normalizeWorkReviewTargets({
    ...item,
    review_target_outputs: reviewTargetOutputs,
  })
  const attempts = item.attempts || []
  const currentAttemptId = resolveCurrentWorkItemAttemptId({ ...item, attempts })
  const clientKey = item.client_key || item.node_key || item.key
  const nodeType = item.node_type
    || (item.item_type === 'runtime_definition' ? 'runtime_definition' : 'work_item')
  return {
    ...item,
    client_key: clientKey,
    assignment_history: item.assignment_history && Array.isArray(item.assignment_history.data)
      ? normalizeList(item.assignment_history, occurrence => occurrence, { page: 1, limit: 20 })
      : null,
    node_type: nodeType,
    capabilities: item.capabilities && typeof item.capabilities === 'object'
      ? { ...item.capabilities }
      : {},
    runtime_navigation: item.runtime_navigation?.is_runtime_branch_item === true
      ? {
        is_runtime_branch_item: true,
        runtime_node_instance_id: item.runtime_navigation.runtime_node_instance_id,
        node_key: item.runtime_navigation.node_key,
        execution_id: item.runtime_navigation.execution_id,
        branch_instance_id: item.runtime_navigation.branch_instance_id,
        work_item_id: item.runtime_navigation.work_item_id || item.id,
      }
      : null,
    key: clientKey,
    work: itemWork,
    work_name: workName,
    display_name: displayName,
    default_assignee_id: assignmentState.assigneeId,
    assignee,
    assignees,
    assignee_sources: sources,
    watcher_ids: assignmentState.watcherIds,
    watchers: assignmentState.watchers,
    attempts,
    current_attempt_id: currentAttemptId,
    input_versions: (item.input_versions || []).map(version => {
      const attempt = attempts.find(row => String(row.id) === String(version.attempt_id ?? version.attempt))
      return {
        ...version,
        validity: isWithdrawnWorkResult(version) || isWithdrawnWorkResult(attempt) ? 'withdrawn' : 'valid',
        withdrawal_id: version.withdrawal_id ?? attempt?.withdrawal_id ?? null,
        original_filename: version.original_filename || version.display_name,
        locked: version.locked || version.immutable,
      }
    }),
    comments: item.comments || [],
    incoming: item.incoming || [],
    outgoing: item.outgoing || [],
    outputs: item.outputs || [],
    runs: item.runs || [],
    input_config: inputConfig,
    rpa_input_mapping: item.rpa_input_mapping || inputConfig.rpa_input_mapping || item.metadata?.rpa_input_mapping || {},
    rpa_output_mapping: item.rpa_output_mapping || inputConfig.rpa_output_mapping || item.metadata?.rpa_output_mapping || {},
    rpa_input_activities: item.rpa_input_activities || [],
    rpa_output_activities: item.rpa_output_activities || [],
    review_target_run_id: item.review_target_run_id || item.metadata?.review_target_run_id || null,
    review_target_outputs: reviewTargetOutputs,
    review_targets: reviewTargets,
    x: Number.isFinite(item.x) ? item.x : Number.isFinite(layout.x) ? layout.x : index % 4,
    y: Number.isFinite(item.y) ? item.y : Number.isFinite(layout.y) ? layout.y : Math.floor(index / 4),
    w: Number.isFinite(item.w) ? item.w : Number.isFinite(layout.w) ? layout.w : 1,
    h: Number.isFinite(item.h) ? item.h : Number.isFinite(layout.h) ? layout.h : 1,
    metadata: {
      ...(item.metadata || {}),
      input_config: inputConfig,
      rpa_item_id: item.rpa_item_id || item.metadata?.rpa_item_id,
      rpa_input_mapping: item.rpa_input_mapping || item.metadata?.rpa_input_mapping || {},
      rpa_output_mapping: item.rpa_output_mapping || item.metadata?.rpa_output_mapping || {},
      review_target_output_id: item.review_target_output_id || item.metadata?.review_target_output_id,
      review_target_run_id: item.review_target_run_id || item.metadata?.review_target_run_id,
      review_target_outputs: reviewTargetOutputs,
      review_targets: reviewTargets,
    },
  }
}

function normalizeRun(run = {}) {
  const runResult = run.run_result || run.metadata || {}
  const rawOutputs = Array.isArray(run.outputs) && run.outputs.length ? run.outputs : runResult.outputs
  const resultOutputs = Array.isArray(rawOutputs)
    ? rawOutputs.map((output, index) => normalizeOutput({
      ...output,
      ...outputWithdrawalContext(output, run),
      id: output.id || output.output_id || `run-${run.id}-${index}`,
      source_work_item_id: output.source_work_item_id || output.work_item_id || run.work_item_id,
      work_item_id: output.work_item_id || run.work_item_id,
      run_id: output.run_id || output.run || run.id,
      created_at: output.created_at || run.created_at,
      updated_at: output.updated_at || run.updated_at,
    }))
    : []
  return {
    ...run,
    work_name: run.work_name || '',
    work_item_name: run.work_item_name || '',
    created_by_username: run.created_by_username || '',
    run_no: run.run_no || run.id,
    run_reason: run.run_reason || run.reason || '',
    started_at: run.started_at || null,
    finished_at: run.finished_at || null,
    run_result: runResult,
    metadata: run.metadata || runResult,
    output_file_id: run.output_file_id || runResult.output_file_id,
    output_ids: Array.isArray(run.output_ids) ? run.output_ids : [],
    outputs: resultOutputs,
    validity: isWithdrawnWorkResult(run) ? 'withdrawn' : 'valid',
    withdrawal_id: run.withdrawal_id ?? null,
    is_official: !isWithdrawnWorkResult(run) && run.is_official === true,
    official_status: isWithdrawnWorkResult(run) ? 'withdrawn' : run.official_status ?? (run.is_official === true ? 'official' : null),
  }
}

function applyOfficialRunStatus(runs = [], outputs = [], officialOutputId = null) {
  const officialRunIds = new Set(outputs
    .filter(output => !isWithdrawnWorkResult(output) && (output.status === 'official' || String(output.id) === String(officialOutputId)))
    .map(output => output.run_id)
    .filter(id => id !== undefined && id !== null && id !== '')
    .map(String))

  if (!officialRunIds.size) return runs
  return runs.map(run => {
    if (isWithdrawnWorkResult(run) || !officialRunIds.has(String(run.id))) return run
    return {
      ...run,
      official_status: 'official',
      is_official: true,
    }
  })
}

function outputWithdrawalContext(output = {}, run = {}) {
  return {
    validity: isWithdrawnWorkResult(output) || isWithdrawnWorkResult(run) ? 'withdrawn' : 'valid',
    withdrawal_id: output.withdrawal_id ?? run.withdrawal_id ?? null,
  }
}

function normalizeOutput(output = {}) {
  const metadata = output.metadata || {}
  const fileId = output.file_id || output.multi_table_data_id || output.data_id || output.mtd_id || metadata.output_file_id || null
  const fileAvailable = Boolean(fileId) || metadata.file_available === true
  const placeholder = metadata.placeholder === true || (!fileId && metadata.file_available === false)
  const placeholderReason = output.placeholder_reason || metadata.placeholder_reason || metadata.message || ''
  const asyncTaskId = output.async_task_id || metadata.async_task_id || null
  return {
    ...output,
    ...outputWithdrawalContext(output),
    work_name: output.work_name || '',
    work_item_name: output.work_item_name || '',
    created_by_username: output.created_by_username || '',
    name: output.name || output.display_name || `Output #${output.id}`,
    display_name: output.display_name || output.name || `Output #${output.id}`,
    file_id: fileId,
    file_available: fileAvailable,
    is_placeholder: placeholder,
    placeholder_reason: placeholderReason,
    placeholder_message: placeholderReason,
    async_task_id: asyncTaskId,
    source_work_item_id: output.source_work_item_id || output.work_item_id || output.work_item,
    run_id: output.run_id || output.run,
  }
}

const RPA_ASYNC_PLACEHOLDER_MESSAGE = 'RPA async task is running.'

function isRpaAsyncPlaceholderOutput(output = {}) {
  const metadata = output.metadata || {}
  const message = output.placeholder_message || metadata.message || metadata.placeholder_reason || ''
  return message === RPA_ASYNC_PLACEHOLDER_MESSAGE
}

function isAsyncRpaRun(run = {}) {
  const runResult = run.run_result || {}
  const metadata = runResult.metadata || run.metadata || {}
  const status = String(run.status || '').toLowerCase()
  const outputs = [
    ...(Array.isArray(run.outputs) ? run.outputs : []),
    ...(Array.isArray(runResult.outputs) ? runResult.outputs : []),
  ]
  return ['initializing', 'queued', 'running', 'waiting'].includes(status) ||
    run.rpa_input_snapshot?.async === true ||
    Boolean(run.async_task_id || metadata.async_task_id) ||
    outputs.some(isRpaAsyncPlaceholderOutput)
}

function hasPendingRpaAsyncOutput({ runs = [], outputs = [] } = {}) {
  const runOutputs = (runs || []).flatMap(run => {
    if (Array.isArray(run.outputs)) return run.outputs
    if (Array.isArray(run.run_result?.outputs)) return run.run_result.outputs
    return []
  })
  return (runs || []).some(isAsyncRpaRun) ||
    [...(outputs || []), ...runOutputs].some(isRpaAsyncPlaceholderOutput)
}

function normalizeIdList(value) {
  const values = Array.isArray(value) ? value : value !== undefined && value !== null ? [value] : []
  return values
    .map(id => Number(id))
    .filter(id => Number.isInteger(id) && id > 0)
}

function rpaRetPksFromRun(run = {}, knownOutputs = []) {
  const result = run.run_result || {}
  const metadata = result.metadata || run.metadata || {}
  const engineResult = metadata.engine_result || result.engine_result || {}
  const candidates = [
    metadata.rpa_ret_pks,
    result.rpa_ret_pks,
    metadata.ret_pks,
    result.ret_pks,
    engineResult.ret_pks,
  ]
  const ids = candidates.flatMap(normalizeIdList)
  knownOutputs.forEach(output => {
    const outputMetadata = output.metadata || {}
    ids.push(...normalizeIdList(outputMetadata.rpa_ret_pks))
    ids.push(...normalizeIdList(outputMetadata.ret_pks))
  })
  return Array.from(new Set(ids.map(String))).map(id => Number(id))
}

function additionalOutputsFromRpaRetPks(run = {}, knownOutputs = []) {
  const result = run.run_result || {}
  const metadata = result.metadata || run.metadata || {}
  const existingFileIds = new Set(knownOutputs
    .map(output => output.file_id || output.multi_table_data_id || output.data_id || output.mtd_id)
    .filter(id => id !== undefined && id !== null && id !== '')
    .map(String))
  const baseName = result.output_display_name || `Run #${run.id} result`
  return rpaRetPksFromRun(run, knownOutputs)
    .map((fileId, index) => ({ fileId, ordinal: index + 1 }))
    .filter(({ fileId }) => !existingFileIds.has(String(fileId)))
    .map(({ fileId, ordinal }) => normalizeOutput({
      ...outputWithdrawalContext({}, run),
      id: `run-${run.id}-ret-${fileId}`,
      is_synthetic: true,
      status: run.status === 'success' ? 'official' : run.status === 'failed' ? 'rejected' : 'candidate',
      output_type: result.output_type || 'rpa_result',
      file_id: fileId,
      display_name: ordinal > 1 ? `${baseName} (${ordinal})` : baseName,
      metadata: {
        ...metadata,
        placeholder: false,
        file_available: true,
        synthetic_from_rpa_ret_pks: true,
        rpa_ret_pk: fileId,
      },
      source_work_item_id: run.work_item_id,
      work_item_id: run.work_item_id,
      run_id: run.id,
      created_at: run.created_at || run.started_at,
      updated_at: run.updated_at || run.finished_at,
    }))
}

function outputsFromRun(run = {}) {
  const result = run.run_result || {}
  const metadata = result.metadata || run.metadata || {}
  if (Array.isArray(run.outputs) && run.outputs.length) return run.outputs.map(output => normalizeOutput({ ...output, ...outputWithdrawalContext(output, run) }))
  if (Array.isArray(result.outputs) && result.outputs.length) {
    return result.outputs.map((output, index) => normalizeOutput({
      ...output,
      ...outputWithdrawalContext(output, run),
      id: output.id || output.output_id || `run-${run.id}-${index}`,
      source_work_item_id: output.source_work_item_id || output.work_item_id || run.work_item_id,
      work_item_id: output.work_item_id || run.work_item_id,
      run_id: output.run_id || output.run || run.id,
      created_at: output.created_at || run.created_at || run.started_at,
      updated_at: output.updated_at || run.updated_at || run.finished_at,
    }))
  }
  const outputIds = Array.isArray(run.output_ids) && run.output_ids.length ? run.output_ids : [null]
  const retPks = rpaRetPksFromRun(run)
  const baseName = result.output_display_name || `Run #${run.id} result`
  return outputIds.map((outputId, index) => normalizeOutput({
    ...outputWithdrawalContext({}, run),
    id: outputId || `run-${run.id}-${index}`,
    status: run.status === 'success' ? 'official' : run.status === 'failed' ? 'rejected' : 'candidate',
    output_type: result.output_type || 'rpa_result',
    file_id: retPks[index] || result.output_file_id || run.output_file_id || null,
    display_name: index > 0 ? `${baseName} (${index + 1})` : baseName,
    metadata: {
      ...metadata,
      placeholder: result.placeholder === true || metadata.placeholder === true,
      file_available: Boolean(retPks[index] || result.output_file_id || run.output_file_id) || metadata.file_available === true,
      message: metadata.message || metadata.placeholder_reason || '',
    },
    source_work_item_id: run.work_item_id,
    work_item_id: run.work_item_id,
    run_id: run.id,
    created_at: run.created_at || run.started_at,
    updated_at: run.updated_at || run.finished_at,
  }))
}

function normalizeNotification(notification = {}) {
  return {
    ...notification,
    read: notification.read !== undefined ? notification.read : Boolean(notification.read_at),
    link: makeNotificationLink(notification) || notification.link,
  }
}

function normalizeTemplatePermission(permission = {}) {
  permission = permission || {}
  return {
    template_id: permission.template_id,
    revision_id: permission.revision_id,
    permission_version: permission.permission_version || 1,
    permissions: permission.permissions || {},
    updated_by: permission.updated_by || null,
    updated_at: permission.updated_at || null,
  }
}

function normalizeExternalEvent(event = {}) {
  event = event || {}
  const errorMessage = typeof event.error === 'string'
    ? event.error
    : event.error?.message || event.result?.error || event.error_message || ''
  return {
    ...event,
    work_id: event.work_id ?? null,
    work_name: event.work_name || '',
    work_item_id: event.work_item_id ?? null,
    work_item_name: event.work_item_name || '',
    event_type: event.event_type || event.event_key || '',
    received_at: event.received_at || event.created_at,
    error_message: errorMessage,
    payload_masked: event.payload_masked || event.payload || {},
  }
}

function safeJsonParse(value, fallback = null) {
  if (!value) return fallback
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

function normalizeRpaInputActivity(activity = {}, index = 0) {
  const activityId = activity.activity_id ?? activity.id ?? activity.block_id
  const activityType = activity.activity_type || activity.type || 'file'
  const rpaPlayType = activity.rpa_play_type || activity.rpaPlayType || activity.rpa_playType || 'unfixed'
  const source = activity.source || {}
  const selectedFiles = source.selectedMultiTableDataList || activity.selectedMultiTableDataList || []
  const isColumnMapping = activity.isColumnMapping === true ||
    activity.is_column_mapping === true ||
    activity.column_mapping_required === true
  const rawColumnGroups = activity.columnSettingItemListGroup || activity.column_setting_item_list_group || []
  const columnGroups = activity.column_groups?.length
    ? activity.column_groups
    : normalizeRpaColumnGroups({ columnSettingItemListGroup: rawColumnGroups })
  const columns = activity.columns?.length
    ? activity.columns
    : columnGroups.flatMap(group => (group.columns || []).map(column => ({
      ...column,
      sheet_id: column.sheet_id ?? group.id,
      sheet_name: column.sheet_name || group.text,
    })))
  return {
    key: String(activityId ?? `activity_${index + 1}`),
    activity_id: activityId != null ? String(activityId) : '',
    label: rpaInputBlockName(activity),
    type: activityType,
    required: activity.required === true,
    editable: rpaPlayType !== 'fixed',
    block_id: activityId,
    block_type: activityType,
    rpa_play_type: rpaPlayType,
    source: {
      ...source,
      selectedMultiTableDataList: selectedFiles,
    },
    selected_files: selectedFiles,
    isColumnMapping,
    is_column_mapping: isColumnMapping,
    column_mapping_required: isColumnMapping,
    columnSettingItemListGroup: JSON.parse(JSON.stringify(rawColumnGroups)),
    column_groups: columnGroups,
    columns,
  }
}

function normalizeRpaColumnGroups(block = {}) {
  const groups = block.columnSettingItemListGroup || (block.columnSettingItemList ? [{
    id: 0,
    text: 'Sheet 1',
    isAbled: true,
    list: block.columnSettingItemList,
  }] : [])
  return (groups || []).map((group, index) => ({
    id: group.id ?? index,
    text: group.text || `Sheet ${index + 1}`,
    enabled: group.isAbled !== false,
    columns: (group.list || []).map(column => ({
      id: column.id,
      key: String(column.id ?? column.fieldName ?? column.text ?? ''),
      label: column.fieldName || column.text || String(column.id ?? ''),
      required: column.required !== false,
      mapped_header: column.mappingHeaderText || '',
      is_mapping: column.isMapping === true,
      word_list: column.wordList || [],
    })).filter(column => column.key),
  })).filter(group => group.enabled)
}

function rpaActivityTypeOf(block = {}) {
  return String(block.activity_type || block.type || '').toLowerCase()
}

function normalizeWorkEvent(event = {}) {
  return {
    ...event,
    work_name: event.work_name || '',
    work_item_name: event.work_item_name || '',
    created_by_username: event.created_by_username || '',
  }
}

function normalizeAggregateEdge(edge = {}) {
  const item = edge.item || {}
  return {
    ...item,
    key: item.key || item.client_key,
    edge_id: edge.id,
    condition: edge.condition || {},
    from_item_id: edge.from_item_id,
    to_item_id: edge.to_item_id,
  }
}

function normalizeWorkItemAggregate(item = {}) {
  const work = normalizeWork(item.work || {
    id: item.work_id,
    name: item.work_name,
  })
  const runs = (item.runs || []).map(normalizeRun)
  const outputById = new Map()
  const pushOutput = output => {
    if (!output) return
    const key = String(output.id || `${output.run_id || 'run'}-${output.file_id || output.display_name || outputById.size}`)
    if (!outputById.has(key)) outputById.set(key, output)
  }
  ;(item.outputs || []).map(normalizeOutput).forEach(pushOutput)
  runs.flatMap(outputsFromRun).forEach(pushOutput)
  const outputs = Array.from(outputById.values())
  runs.flatMap(run => additionalOutputsFromRpaRetPks(
    run,
    outputs.filter(output => String(output.run_id) === String(run.id))
  )).forEach(pushOutput)
  return normalizeWorkItem({
    ...item,
    incoming: (item.incoming || []).map(normalizeAggregateEdge),
    outgoing: (item.outgoing || []).map(normalizeAggregateEdge),
    runs,
    outputs: Array.from(outputById.values()),
  }, work)
}

function normalizeRpaOutputActivity(activity = {}, index = 0) {
  const activityId = activity.activity_id ?? activity.id ?? activity.block_id
  return {
    key: String(activityId ?? `output_${index + 1}`),
    activity_id: activityId != null ? String(activityId) : '',
    activity_type: activity.activity_type || activity.type || 'output',
    activity_name: activity.activity_name || activity.edited_name || activity.name || `Output ${activityId ?? index + 1}`,
    rpa_play_type: activity.rpa_play_type || activity.rpaPlayType || activity.rpa_playType || '',
  }
}

function extractRpaOutputActivities(flowList = []) {
  return flowList
    .filter(block => ['output', 'alert'].includes(rpaActivityTypeOf(block)))
    .map(normalizeRpaOutputActivity)
}

function normalizeRpaInputDefinition(raw = {}) {
  const rpa = safeJsonParse(raw.rpa, raw.rpa || {})
  const flowList = rpa.rpaFlowList || []
  const outputActivities = raw.rpa_output_activities || extractRpaOutputActivities(flowList)
  const explicitInput = raw.input || rpa.input
  if (explicitInput && explicitInput.use_input_fields) {
    const fields = (explicitInput.fields || [])
      .filter(field => field.activity_id != null || field.key != null)
      .map(field => normalizeRpaInputActivity({
        ...field,
        activity_id: field.activity_id ?? field.key,
        activity_type: field.type || 'file',
        required: field.required !== false,
      }))
    return {
      id: raw.id,
      name: raw.name || raw.title || `워크플로우 #${raw.id || '-'}`,
      version_major: raw.version_major,
      version_minor: raw.version_minor,
      updated_at: raw.updated_at,
      created_at: raw.created_at,
      input: {
        schema_version: explicitInput.schema_version || '2.1',
        use_input_fields: true,
        fields: mergeRpaInputFieldsWithFlowBlocks(fields, flowList),
      },
      output: {
        activities: outputActivities,
      },
      source: 'rpa_item_input',
    }
  }

  const fields = flowList
    .filter(block => RPA_INPUT_ACTIVITY_TYPES.has(rpaActivityTypeOf(block)))
    .map(block => {
      const isColumnMapping = block.isColumnMapping === true || block.is_column_mapping === true || block.column_mapping_required === true
      const groups = isColumnMapping ? normalizeRpaColumnGroups(block) : []
      return normalizeRpaInputActivity({
        activity_id: block.id,
        activity_type: block.type,
        activity_name: rpaInputBlockName(block),
        rpaPlayType: block.rpaPlayType || 'unfixed',
        selectedMultiTableDataList: block.selectedMultiTableDataList || [],
        type: 'file',
        required: false,
        is_column_mapping: isColumnMapping,
        column_mapping_required: isColumnMapping,
        columnSettingItemListGroup: block.columnSettingItemListGroup || [],
        column_groups: groups,
        columns: isColumnMapping
          ? groups.flatMap(group => group.columns.map(column => ({
            ...column,
            sheet_id: group.id,
            sheet_name: group.text,
          })))
          : [],
      })
    })

  return {
    id: raw.id,
    name: raw.name || raw.title || `워크플로우 #${raw.id || '-'}`,
    version_major: raw.version_major,
    version_minor: raw.version_minor,
    updated_at: raw.updated_at,
    created_at: raw.created_at,
    input: {
      schema_version: '2.1',
      use_input_fields: fields.length > 0,
      fields: mergeRpaInputFieldsWithFlowBlocks(fields, flowList),
    },
    output: {
      activities: outputActivities,
    },
    source: 'rpa_flow_column_mapping',
  }
}

let cachedAssigneeProjectId = null
let cachedAssigneeCandidates = []
let cachedAssigneeOrganizations = []

function makeNotificationLink(notification) {
  const projectId = notification.project_id || notification.work?.project_id || notification.work_item?.project_id
  if (!projectId) return null
  if (notification.work_item_id) {
    const workId = notification.work_id || notification.work?.id || notification.work_item?.work_id || notification.work_item?.work?.id
    return workItemFlowPath({ projectId, workId, workItemId: notification.work_item_id })
  }
  if (notification.work_id) return `/work-management/${projectId}/works/${notification.work_id}`
  return `/work-management/${projectId}/notifications`
}

function normalizeList(response, normalizer, fallbackMeta = {}) {
  return {
    data: asArray(response).map(normalizer).filter(Boolean),
    meta: normalizePaginationMeta(metaOf(response) || {}, fallbackMeta),
  }
}

function normalizeWorkEventList(response, fallbackMeta = {}) {
  const normalized = normalizeWorkEventListPayload(response || {})
  return {
    data: normalized.data,
    meta: normalizePaginationMeta(metaOf(response) || {}, fallbackMeta),
    filterOptions: normalized.filterOptions,
  }
}

async function collectAllPages(fetchPage, params = {}, identity = row => row?.id) {
  const baseParams = paginationParams({ ...params, page: 1, limit: 1000 }, 1000)
  const first = await fetchPage(baseParams)
  const maxPage = first.meta.maxPage
  const pages = [first]
  for (let page = 2; page <= maxPage; page += 1) {
    pages.push(await fetchPage({ ...baseParams, page }))
  }
  const seen = new Set()
  const data = pages.flatMap(result => result.data).filter((row, index) => {
    const id = identity(row)
    const key = id === undefined || id === null ? `__index_${index}` : String(id)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return {
    data,
    meta: normalizePaginationMeta({ ...first.meta, total: data.length, page: 1 }, baseParams),
  }
}

function normalizeTemplateResponse(response, current = null) {
  const data = unwrap(response, null)
  if (!data) return current
  const base = current || {}
  return normalizeTemplate({
    ...base,
    ...data,
    graph: data.graph || base.graph || null,
    items: data.items || base.items || [],
    flows: data.flows || data.edges || base.flows || base.edges || [],
  })
}

function templateBasePayload(payload) {
  return buildWorkTemplateBasePayload(payload)
}

function itemClientKey(item = {}, index = 0) {
  return item.client_key || item.key || String.fromCharCode(65 + index)
}

function itemLocalKey(item = {}, index = 0) {
  return String(item.id || item.local_id || item.i || itemClientKey(item, index))
}

function itemDisplayName(item = {}, index = 0) {
  const key = itemClientKey(item, index)
  return `${key}. ${item.name || '할일'}`
}

function itemOutputActivities(item = {}) {
  const activities = item.rpa_output_activities || item.input_config?.rpa_output_activities || item.metadata?.rpa_output_activities || []
  return Array.isArray(activities) ? activities : []
}

function rpaOutputActivities(item = {}) {
  return itemOutputActivities(item).filter(activity => String(activity.activity_type || '').toLowerCase() === 'output')
}

function outputMapping(item = {}) {
  return item.rpa_output_mapping || item.input_config?.rpa_output_mapping || item.metadata?.rpa_output_mapping || {}
}

function outputMappingActivityIds(mapping = {}) {
  const raw = mapping.output_activity_ids || mapping.activity_ids || mapping.output_activity_id || mapping.activity_id || []
  const values = Array.isArray(raw) ? raw : raw ? [raw] : []
  return values.map(value => String(value)).filter(Boolean)
}

function templateEdgesForValidation(flows = [], items = []) {
  const keyById = new Map()
  items.forEach((item, index) => {
    const clientKey = itemClientKey(item, index)
    ;[item.id, item.local_id, item.i, clientKey].forEach(id => {
      if (id !== undefined && id !== null && id !== '') keyById.set(String(id), clientKey)
    })
  })
  const keyOf = value => {
    if (value === undefined || value === null || value === '') return ''
    return keyById.get(String(value)) || String(value)
  }
  return (flows || []).map(flow => ({
    from_client_key: flow.from_client_key || keyOf(flow.from_item_template_id || flow.from_item_id || flow.start_item_id),
    to_client_key: flow.to_client_key || keyOf(flow.to_item_template_id || flow.to_item_id || flow.end_item_id),
  })).filter(edge => edge.from_client_key && edge.to_client_key)
}

function throwTemplateRpaOutputMappingError(message, extra = {}) {
  throw {
    code: 'WORK_TEMPLATE_RPA_OUTPUT_MAPPING_INVALID',
    message,
    extra,
  }
}

function validateTemplateRpaOutputMappings(items = [], flows = []) {
  if (!Array.isArray(items) || !items.length) return
  const itemsByClientKey = new Map()
  items.forEach((item, index) => {
    itemsByClientKey.set(String(itemClientKey(item, index)), item)
    itemsByClientKey.set(itemLocalKey(item, index), item)
  })
  const edges = templateEdgesForValidation(flows, items)

  items.forEach((item, index) => {
    if (item.item_type !== 'file_review') return
    const mapping = outputMapping(item)
    const incomingRpaItems = edges
      .filter(edge => String(edge.to_client_key) === String(itemClientKey(item, index)))
      .map(edge => itemsByClientKey.get(String(edge.from_client_key)))
      .filter(source => source?.item_type === 'rpa_run')
    const sourceKey = mapping.source_item_client_key || mapping.source_client_key || mapping.from_client_key
    const sourceItem = sourceKey
      ? itemsByClientKey.get(String(sourceKey))
      : incomingRpaItems.length === 1
        ? incomingRpaItems[0]
        : null

    if (!sourceItem && (incomingRpaItems.length || Object.keys(mapping).length)) {
      throwTemplateRpaOutputMappingError(
        `${itemDisplayName(item, index)}의 검토 대상 RPA 실행 항목을 선택해 주세요.`,
        { item_client_key: itemClientKey(item, index) }
      )
    }

    if (!sourceItem) return
    if (sourceItem.item_type !== 'rpa_run') {
      throwTemplateRpaOutputMappingError(
        `${itemDisplayName(item, index)}의 검토 대상은 RPA 실행 항목이어야 합니다.`,
        { item_client_key: itemClientKey(item, index), source_client_key: sourceKey }
      )
    }

    const outputActivities = rpaOutputActivities(sourceItem)
    if (!outputActivities.length) return
    if (!outputMappingActivityIds(mapping).length) {
      throwTemplateRpaOutputMappingError(
        `${itemDisplayName(item, index)}의 검토 대상 output activity를 선택해 주세요.`,
        {
          item_client_key: itemClientKey(item, index),
          source_client_key: itemClientKey(sourceItem, items.indexOf(sourceItem)),
          output_activity_ids: outputActivities.map(activity => activity.activity_id).filter(Boolean),
        }
      )
    }
  })
}

function apiErrorData(error) {
  if (error?.response?.data) return error.response.data
  if (error?.error && typeof error.error === 'object') return error.error
  return error
}

function apiErrorCode(error) {
  const data = apiErrorData(error)
  return data?.code || data?.error?.code || ''
}

const WORK_TREE_VALIDATION_MESSAGES = {
  FLOW_EDGE_DUPLICATED: '동일한 연결이 중복되어 있습니다. 중복 연결을 제거해 주세요.',
  FLOW_SELF_EDGE_NOT_ALLOWED: '같은 할일을 자기 자신과 연결할 수 없습니다. 해당 연결을 제거해 주세요.',
  FLOW_CYCLE_NOT_ALLOWED: 'Flow에 순환 연결이 있습니다. 순환 연결을 제거한 뒤 다시 저장해 주세요.',
  WORK_TREE_EDGE_ITEM_NOT_FOUND: '연결된 할일을 찾을 수 없습니다. 삭제되거나 Key가 변경된 할일의 연결을 확인해 주세요.',
}

function isWorkTreeValidationError(error) {
  return Object.prototype.hasOwnProperty.call(WORK_TREE_VALIDATION_MESSAGES, apiErrorCode(error))
}

function isObjectPermissionError(error) {
  return [
    'WORK_ACCESS_DENIED',
    'WORK_ITEM_ACCESS_DENIED',
    'WORK_RUN_ACCESS_DENIED',
    'WORK_OUTPUT_ACCESS_DENIED',
    'WORK_NOTIFICATION_ACCESS_DENIED',
    'WORK_TEMPLATE_ACCESS_DENIED',
    'WORK_TEMPLATE_PROJECT_ACCESS_DENIED',
    'WORK_PROJECT_ACCESS_DENIED',
    'EXTERNAL_EVENT_ACCESS_DENIED',
    'EXTERNAL_EVENT_PAYLOAD_ACCESS_DENIED',
    'RPA_OUTPUT_FILE_ACCESS_DENIED',
    'NOTIFICATION_ACCESS_DENIED',
    'WORK_RULE_BRANCH_FLOW_ACCESS_DENIED',
  ].includes(apiErrorCode(error))
}

function isVersionConflictError(error) {
  return [
    'TREE_VERSION_CONFLICT',
    'PERMISSION_VERSION_CONFLICT',
    'EXTRA_VERSION_CONFLICT',
    'WORK_TEMPLATE_BRANCH_FLOW_PERMISSION_CONFLICT',
  ].includes(apiErrorCode(error))
}

function normalizeWorkPermissions(rawWork = {}, rawFlow = {}) {
  const source = rawWork.permissions || rawFlow.permissions || {}
  const isFallback = !rawWork.permissions && !rawFlow.permissions
  return {
    _fallback: isFallback,
    can_read_flow: source.can_read_flow !== false,
    can_edit_flow: source.can_edit_flow !== false,
    can_read_item: source.can_read_item !== false,
    can_edit_item: source.can_edit_item !== false,
    can_read_output: source.can_read_output !== false,
    can_execute_rpa: source.can_execute_rpa !== false,
    can_set_official: source.can_set_official !== false,
  }
}

function templateItemsPayload(items = []) {
  return items.map((item, index) => ({
    client_key: item.client_key || item.key || String.fromCharCode(65 + index),
    name: item.name,
    item_type: item.item_type === 'manual_check' ? 'manual' : item.item_type,
    ...(item.execution_mode ? { execution_mode: item.execution_mode } : {}),
    required: item.required !== false,
    completion_policy: item.completion_policy || 'any_one',
    activation_policy: item.activation_policy ?? 'all',
    activation_rule: item.activation_rule ?? null,
    due_rule: item.due_rule || {},
    assignees: buildChangedWorkItemAssignments(item),
    input_config: {
      ...(item.input_config || {}),
      _layout: {
        x: Number.isFinite(item.x) ? item.x : index % 4,
        y: Number.isFinite(item.y) ? item.y : Math.floor(index / 4),
        w: Number.isFinite(item.w) ? item.w : 1,
        h: Number.isFinite(item.h) ? item.h : 1,
      },
    },
    notification_rule: item.notification_rule || {},
    rpa_item_id: item.rpa_item_id || item.input_config?.rpa_item_id || null,
    rpa_input_mapping: item.rpa_input_mapping || item.input_config?.rpa_input_mapping || {},
    rpa_output_mapping: item.rpa_output_mapping || item.input_config?.rpa_output_mapping || {},
    order: Number.isFinite(item.order) ? item.order : index,
  }))
}

function templateEdgesPayload(flows = [], items = []) {
  const keyById = new Map()
  items.forEach((item, index) => {
    const key = item.client_key || item.key || String.fromCharCode(65 + index)
    ;[item.id, item.local_id, item.from_item_template_id, item.to_item_template_id].forEach(id => {
      if (id !== undefined && id !== null) keyById.set(String(id), key)
    })
  })
  const keyOf = value => {
    if (value === undefined || value === null) return null
    return keyById.get(String(value)) || value
  }
  return flows.map(flow => ({
    from_client_key: flow.from_client_key || keyOf(flow.from_item_template_id || flow.from_item_id || flow.start_item_id),
    to_client_key: flow.to_client_key || keyOf(flow.to_item_template_id || flow.to_item_id || flow.end_item_id),
    condition: flow.condition || 'completed',
    start_item_position: flow.start_item_position || 'right',
    end_item_position: flow.end_item_position || 'left',
  })).filter(edge => edge.from_client_key && edge.to_client_key)
}

function legacyTemplateGraph(payload = {}, treeVersion = 0) {
  const items = payload.items || []
  const nodes = templateItemsPayload(items).map((item, index) => {
    const {
      client_key: clientKey,
      order,
      input_config: inputConfig = {},
      ...workItem
    } = item
    const { _layout: storedLayout, ...cleanInputConfig } = inputConfig
    const layout = storedLayout || {
      x: Number.isFinite(items[index]?.x) ? items[index].x : index % 4,
      y: Number.isFinite(items[index]?.y) ? items[index].y : Math.floor(index / 4),
      w: Number.isFinite(items[index]?.w) ? items[index].w : 1,
      h: Number.isFinite(items[index]?.h) ? items[index].h : 1,
    }
    return {
      client_key: clientKey,
      node_type: 'work_item',
      layout,
      order,
      work_item: {
        ...workItem,
        input_config: cleanInputConfig,
      },
    }
  })
  const edges = templateEdgesPayload(payload.flows || payload.edges || [], items).map(edge => ({
    from_node_key: edge.from_client_key,
    to_node_key: edge.to_client_key,
    condition: edge.condition,
    start_item_position: edge.start_item_position,
    end_item_position: edge.end_item_position,
  }))
  return normalizeTemplateGraph({
    tree_version: treeVersion,
    nodes,
    edges,
  })
}

function templateGraphForWrite(payload = {}, treeVersion = 0) {
  if (payload.graph && typeof payload.graph === 'object') {
    return normalizeTemplateGraph({
      graph: payload.graph,
      tree_version: treeVersion,
    })
  }
  return legacyTemplateGraph(payload, treeVersion)
}

function shouldWriteTemplateGraph(payload = {}) {
  return Boolean(
    payload.graph
    || Object.prototype.hasOwnProperty.call(payload, 'items')
    || Object.prototype.hasOwnProperty.call(payload, 'flows')
    || Object.prototype.hasOwnProperty.call(payload, 'edges')
  )
}

function validateTemplateGraphRpaOutputMappings(payload = {}) {
  if (shouldWriteTemplateGraph(payload)) {
    buildTemplateGraphPayload({ graph: templateGraphForWrite(payload) })
  }
  if (!payload.graph) {
    validateTemplateRpaOutputMappings(payload.items || [], payload.flows || payload.edges || [])
    return
  }
  const graph = normalizeTemplateGraph(payload)
  validateTemplateRpaOutputMappings(
    templateGraphCanvasItems(graph),
    templateGraphCanvasFlows(graph)
  )
}

function workItemsPayload(items = []) {
  return items.map((item, index) => ({
    id: item.id,
    client_key: item.client_key || item.key || String.fromCharCode(65 + index),
    name: item.name,
    item_type: item.item_type === 'manual_check' ? 'manual' : item.item_type,
    required: item.required !== false,
    completion_policy: item.completion_policy || 'any_one',
    due_at: item.due_at || null,
    assignees: buildChangedWorkItemAssignments(item),
    input_config: {
      ...(item.input_config || {}),
      _layout: {
        x: Number.isFinite(item.x) ? item.x : index % 4,
        y: Number.isFinite(item.y) ? item.y : Math.floor(index / 4),
        w: Number.isFinite(item.w) ? item.w : 1,
        h: Number.isFinite(item.h) ? item.h : 1,
      },
    },
    notification_rule: item.notification_rule || {},
    rpa_item_id: item.rpa_item_id || item.input_config?.rpa_item_id || item.metadata?.rpa_item_id || null,
    rpa_input_mapping: item.rpa_input_mapping || item.input_config?.rpa_input_mapping || item.metadata?.rpa_input_mapping || {},
    rpa_output_mapping: item.rpa_output_mapping || item.input_config?.rpa_output_mapping || item.metadata?.rpa_output_mapping || {},
    order: Number.isFinite(item.order) ? item.order : index,
  }))
}

function workEdgesPayload(flows = [], items = []) {
  const clientKeys = new Set()
  const clientKeyByItemId = new Map()
  items.forEach((item, index) => {
    const clientKey = item.client_key || item.key || String.fromCharCode(65 + index)
    clientKeys.add(String(clientKey))
    const references = [item.id, item.local_id, item.i]
    references.forEach(reference => {
      if (reference !== undefined && reference !== null && reference !== '') {
        clientKeyByItemId.set(String(reference), clientKey)
      }
    })
  })
  const endpointKey = (canonicalReferences, itemIdReferences) => {
    const canonicalReference = canonicalReferences.find(value => value !== undefined && value !== null && value !== '')
    if (canonicalReference !== undefined) {
      const canonicalKey = String(canonicalReference)
      return clientKeys.has(canonicalKey)
        ? canonicalKey
        : clientKeyByItemId.get(canonicalKey) || canonicalKey
    }
    const itemIdReference = itemIdReferences.find(value => value !== undefined && value !== null && value !== '')
    if (itemIdReference === undefined) return null
    return clientKeyByItemId.get(String(itemIdReference)) || String(itemIdReference)
  }
  return flows.map(flow => {
    return {
      from_node_key: endpointKey([
        flow.from_node_key, flow.from_client_key,
      ], [
        flow.from_item_id,
        flow.from_item_template_id,
        flow.start_item_id,
      ]),
      to_node_key: endpointKey([
        flow.to_node_key, flow.to_client_key,
      ], [
        flow.to_item_id,
        flow.to_item_template_id,
        flow.end_item_id,
      ]),
      condition: flow.condition || 'completed',
      start_item_position: flow.start_item_position || 'right',
      end_item_position: flow.end_item_position || 'left',
    }
  }).filter(edge => edge.from_node_key && edge.to_node_key)
}

function editableWorkTreeGraph(items = [], flows = []) {
  const runtimeReferences = new Set()
  const editableItems = items.filter(item => {
    const runtime = item?.node_type === 'runtime_definition' || item?.item_type === 'runtime_definition'
    if (!runtime) return true
    ;[item.id, item.local_id, item.i, item.client_key, item.key, item.canvas_id].forEach(reference => {
      if (reference !== undefined && reference !== null && reference !== '') {
        runtimeReferences.add(String(reference))
      }
    })
    return false
  })
  const editableFlows = flows.filter(flow => {
    const references = [
      flow.from_node_key, flow.from_client_key, flow.start_item_id,
      flow.from_item_template_id, flow.from_item_id,
      flow.to_node_key, flow.to_client_key, flow.end_item_id,
      flow.to_item_template_id, flow.to_item_id,
    ]
    return !references.some(reference => runtimeReferences.has(String(reference)))
  })
  return { items: editableItems, flows: editableFlows }
}

function apiErrorMessage(error) {
  const data = apiErrorData(error)
  const code = apiErrorCode(error)
  const message = data?.message || data?.error?.message
  const details = data?.error && typeof data.error === 'object' ? data.error : data
  if (isWorkTreeValidationError(error)) {
    const source = details?.from_node_key
    const target = details?.to_node_key
    const connection = source || target ? `연결 ${source || '?'} → ${target || '?'}: ` : ''
    return `${connection}${WORK_TREE_VALIDATION_MESSAGES[code]}`
  }
  const firstDemoMessage = workManagementFirstDemoErrorMessage(error, code)
  if (firstDemoMessage) return firstDemoMessage
  const activationMessages = {
    ACTIVATION_POLICY_NOT_SUPPORTED: '지원하는 활성화 정책(all, any, conditional)을 확인해 주세요.',
    ACTIVATION_RULE_INVALID: '활성화 조건식의 형식을 확인해 주세요. 조건부 정책에는 비어 있지 않은 조건식이 필요하고, all·any 정책의 조건식은 비워야 합니다.',
    ACTIVATION_RULE_SOURCE_MISMATCH: '활성화 조건식의 선행 할일 Key와 연결 조건이 실제 들어오는 연결과 일치하는지 확인해 주세요.',
    ACTIVATION_RULE_LIMIT_EXCEEDED: '활성화 조건식은 중첩 깊이 8단계, 개별 조건 64개까지 사용할 수 있습니다.',
    ACTIVATION_ROOT_POLICY_INVALID: '선행 연결이 없는 할일은 활성화 정책 all만 사용할 수 있으며 조건식은 비워야 합니다.',
  }
  if (activationMessages[code]) {
    const target = details?.client_key ? `할일 ${details.client_key}: ` : ''
    return `${target}${activationMessages[code]}`
  }
  if (code === 'TREE_VERSION_CONFLICT') return 'Work tree가 변경되었습니다. 최신 내용을 다시 불러온 뒤 저장해 주세요.'
  if (code === 'WORK_FORM_SCHEMA_REQUIRED') return '폼 설정을 다시 확인해야 합니다. 작성한 내용은 유지되며 최신 상태를 조회합니다.'
  if (code === 'WORK_FORM_SCHEMA_CONFLICT') return '폼 설정 또는 입력 회차가 변경되었습니다. 작성한 내용을 보존하고 최신 상태를 확인해 주세요. 실행 중 발생했다면 선행 폼 입력의 철회·재입력 가능 여부를 확인해 주세요.'
  if (code === 'PERMISSION_VERSION_CONFLICT') return '권한 설정이 변경되었습니다. 최신 권한을 다시 불러와 주세요.'
  if (code === 'WORK_ALREADY_PUBLISHED') return '이미 발행된 회차입니다.'
  if (code === 'WORK_ITEM_EXECUTION_MODE_UNSUPPORTED') return '지원하는 실행 방식(수동 또는 자동)을 선택해 주세요.'
  if (code === 'WORK_ITEM_SYSTEM_AUTO_TYPE_REQUIRED') return '자동 실행은 RPA 실행 항목에서만 사용할 수 있습니다.'
  if (code === 'WORK_ITEM_SYSTEM_AUTO_RPA_REQUIRED') return '자동 실행할 유효한 RPA를 선택해 주세요.'
  if (code === 'WORK_ITEM_SYSTEM_AUTO_ASSIGNEE_NOT_ALLOWED') return '자동 실행 항목에서는 실행 담당자를 제거해 주세요. 참조자는 유지할 수 있습니다.'
  if (code === 'WORK_ITEM_SYSTEM_AUTO_INPUT_MAPPING_INVALID') {
    const missingFields = Array.isArray(details?.missing_fields) ? details.missing_fields.filter(Boolean) : []
    return missingFields.length
      ? `자동 실행에 필요한 RPA 입력 연결을 확인해 주세요. (${missingFields.join(', ')})`
      : '자동 실행에 필요한 RPA 입력 연결을 확인해 주세요.'
  }
  if (code === 'WORK_ITEM_SYSTEM_AUTO_MANUAL_RUN_NOT_ALLOWED') return '자동 실행 항목은 수동으로 실행할 수 없습니다. 최신 실행 상태를 다시 불러와 주세요.'
  if (code === 'WORK_ITEM_SYSTEM_AUTO_STATUS_CHANGE_NOT_ALLOWED') return '자동 실행 항목의 상태는 시스템이 변경합니다. 최신 상태를 다시 불러와 주세요.'
  if (code === 'RPA_INPUT_VALIDATION_FAILED') return '워크플로우 실행 입력 매핑을 확인해 주세요.'
  if (code === 'RPA_INPUT_MAPPING_REQUIRED') return '필수 워크플로우 입력 매핑을 모두 연결해 주세요.'
  if (code === 'RPA_INPUT_SOURCE_NOT_FOUND') return '매핑된 원본 할일을 찾을 수 없습니다.'
  if (code === 'RPA_INPUT_SOURCE_NOT_COMPLETED') return '매핑된 원본 할일이 완료된 뒤 실행할 수 있습니다.'
  if (code === 'RPA_INPUT_VERSION_NOT_FOUND') return '매핑된 원본 할일의 입력 버전이 없습니다.'
  if (code === 'RPA_INPUT_FILE_NOT_FOUND') return '매핑된 원본 입력 버전에 파일이 없습니다.'
  if (code === 'RPA_INPUT_COLUMN_MAPPING_REQUIRED') return '컬럼 매핑이 필요한 워크플로우 입력의 컬럼 설정을 확인해 주세요.'
  if (code === 'RPA_INPUT_COLUMN_MAPPING_INVALID') return '워크플로우 입력의 컬럼 매핑 형식과 매핑 헤더를 확인해 주세요.'
  if (code === 'WORK_ITEM_NOT_RUNNABLE') return '현재 상태에서는 워크플로우를 실행할 수 없습니다.'
  if (code === 'RPA_OUTPUT_FILE_NOT_FOUND') return 'RPA 결과 파일을 찾을 수 없습니다. output_file_id를 확인해 주세요.'
  if (code === 'RPA_OUTPUT_MAPPING_NOT_FOUND') return 'RPA 실행 결과와 템플릿 산출물 매핑이 일치하지 않습니다. 템플릿의 검토 대상 output activity 설정을 확인해 주세요.'
  if (code === 'WORK_TEMPLATE_RPA_OUTPUT_MAPPING_INVALID') return message || '템플릿의 RPA output activity 매핑을 확인해 주세요.'
  if (code === 'RUN_REVIEW_TARGETS_NOT_COMPLETED') return '이 실행 회차의 후행 검토 대상이 모두 완료된 뒤 공식 결과로 지정할 수 있습니다.'
  if (code === 'RUN_OUTPUT_NOT_FOUND') return '공식 지정 또는 롤백할 실행 결과물이 없습니다.'
  if (code === 'ASSIGNEE_USER_IDS_PARAM_INVALID') return '담당자 필터 값을 확인해 주세요.'
  if (code === 'WORK_ASSIGNEE_ROLE_INVALID') return '담당자 또는 참조자 역할 값을 확인해 주세요.'
  if (code === 'WORK_ASSIGNEE_DUPLICATED') return '같은 사용자를 담당자와 참조자로 동시에 지정할 수 없습니다.'
  if (code === 'WORK_PRIMARY_ASSIGNEE_REQUIRED') return '할일 담당자를 지정해 주세요.'
  if (code === 'WORK_PRIMARY_ASSIGNEE_MULTIPLE') return '할일 담당자는 한 명만 지정할 수 있습니다.'
  if (code === 'EXPECTED_TREE_VERSION_REQUIRED') return '최신 업무 정보를 다시 불러온 뒤 요청해 주세요.'
  if (code === 'WORK_ITEM_STATUS_NOT_ALLOWED') return '현재 API에서 허용하지 않는 상태 변경입니다.'
  if (code === 'EXTRA_VERSION_CONFLICT') return 'Extra field가 변경되었습니다. 최신 내용을 다시 불러온 뒤 저장해 주세요.'
  if (code === 'WORK_TEMPLATE_PERMISSION_INVALID') return '권한 입력값을 확인해 주세요.'
  if (code === 'WORK_TEMPLATE_BRANCH_FLOW_RULE_NOT_FOUND') return '삭제되었거나 이름이 변경된 Rule의 Branch 조회 권한 매핑을 확인해 주세요.'
  if (code === 'WORK_TEMPLATE_BRANCH_FLOW_SUBJECT_NOT_ASSIGNABLE') return '프로젝트에서 선택할 수 없는 사용자 또는 그룹이 Branch 조회 권한에 포함되어 있습니다.'
  if (code === 'WORK_TEMPLATE_BRANCH_FLOW_PERMISSION_CONFLICT') return 'Branch 조회 권한이 변경되었습니다. 최신 권한을 다시 불러온 뒤 재시도해 주세요.'
  if (code === 'WORK_TEMPLATE_BRANCH_FLOW_PERMISSION_INVALID') return 'Branch 조회 권한 입력값을 확인해 주세요.'
  if (code === 'WORK_ORGANIZATION_ASSIGNEE_NOT_FOUND') return '선택한 조직이 존재하지 않습니다. 조직 목록을 다시 불러와 주세요.'
  if (code === 'WORK_ORGANIZATION_ASSIGNEE_OUT_OF_SCOPE') return '프로젝트에서 담당자로 지정할 수 없는 조직입니다. 조직 목록을 다시 확인해 주세요.'
  if (code === 'WORK_TEMPLATE_ASSIGNEES_INVALID') return '담당 사용자·조직과 하위 조직 포함 설정을 확인해 주세요.'
  if (code === 'WORK_ASSIGNMENT_CHANGE_KEY_CONFLICT') return '담당 변경 요청이 이전 요청과 충돌했습니다. 최신 업무를 조회한 후 변경 내용을 다시 확인해 주세요.'
  if (code === 'WORK_ASSIGNEE_NOT_FOUND' || code === 'WORK_TEMPLATE_ASSIGNEE_NOT_FOUND') return '할일 담당자 정보를 확인해 주세요.'
  if (code === 'MANUAL_INPUT_ITEM_TYPE_INVALID' || code === 'MANUAL_INPUT_TYPE_INVALID') return '수동 입력을 등록할 수 없는 할일입니다.'
  if (code === 'WORK_ACCESS_DENIED') return '이 업무에 접근할 권한이 없습니다.'
  if (code === 'WORK_RULE_BRANCH_FLOW_ACCESS_DENIED') return '이 Rule의 Branch Flow를 조회할 권한이 없습니다.'
  if (code === 'WORK_ITEM_ACCESS_DENIED') return '이 할일에 접근하거나 수정할 권한이 없습니다.'
  if (code === 'WORK_RUN_ACCESS_DENIED') return '이 실행 이력에 접근하거나 검토할 권한이 없습니다.'
  if (code === 'WORK_OUTPUT_ACCESS_DENIED') return '이 산출물에 접근하거나 수정할 권한이 없습니다.'
  if (code === 'WORK_NOTIFICATION_ACCESS_DENIED') return '이 업무의 전체 알림을 볼 권한이 없습니다.'
  if (code === 'WORK_TEMPLATE_PROJECT_REQUIRED') return '템플릿을 생성할 프로젝트 정보가 필요합니다.'
  if (code === 'WORK_TEMPLATE_PROJECT_ACCESS_DENIED') return '이 프로젝트에 템플릿을 생성할 권한이 없습니다.'
  if (code === 'WORK_PROJECT_ACCESS_DENIED') return '이 프로젝트의 작업관리 객체에 접근할 권한이 없습니다.'
  if (code === 'PROJECT_ID_PARAM_INVALID') return '프로젝트 필터 값을 확인해 주세요.'
  if (code === 'WORK_TEMPLATE_ACCESS_DENIED') return '이 업무 템플릿에 접근하거나 수정할 권한이 없습니다.'
  if (code === 'EXTERNAL_EVENT_ACCESS_DENIED') return '이 외부 이벤트에 접근할 권한이 없습니다.'
  if (code === 'EXTERNAL_EVENT_PAYLOAD_ACCESS_DENIED') return '외부 이벤트 payload 조회 권한이 없습니다.'
  if (code === 'RPA_OUTPUT_FILE_ACCESS_DENIED') return 'RPA 결과 파일에 접근할 권한이 없습니다.'
  if (code === 'EXTERNAL_EVENT_PAYLOAD_VIEW_INVALID') return '외부 이벤트 payload view 값을 확인해 주세요.'
  if (code === 'PAGINATION_PARAM_INVALID') return '페이지 요청 값을 확인해 주세요.'
  if (code === 'PROJECT_ID_REQUIRED') return '프로젝트 정보를 확인해 주세요.'
  if (code === 'WORK_EVENT_FILTER_INVALID') return '로그 필터 값을 확인해 주세요.'
  if (code === 'WORK_EVENT_RANGE_INVALID') return '로그 조회 기간을 확인해 주세요.'
  if (code === 'WORK_EVENT_ORDERING_INVALID') return '로그 정렬 값을 확인해 주세요.'
  if (code === 'WORK_EVENT_ACCESS_DENIED') return '이 업무의 이벤트를 조회할 권한이 없습니다.'
  if (code === 'WORK_NOT_FOUND') return '조회할 수 있는 업무를 찾지 못했습니다.'
  if (code === 'NOTIFICATION_SCOPE_INVALID') return '알림 조회 범위를 확인해 주세요.'
  return message || '요청 처리 중 오류가 발생했습니다.'
}

function normalizePreviewData(file = {}, preview = {}) {
  const data = {
    ...file,
    ...preview,
    id: preview.id || file.id,
    name: preview.name || file.name || file.original_filename || file.display_name,
    data_type: 'multiTableData',
    currTableIdx: 0,
  }
  data.tableSelectList = (data.table_datas || []).map((table, index) => ({
    text: table.title || `시트 ${index + 1}`,
    value: index,
  }))
  return data
}

async function buildWorkDetail(workId, pagination = {}, options = {}) {
  const runsParams = paginationParams(pagination.runs || {}, 50)
  const outputsParams = paginationParams(pagination.outputs || {}, 50)
  const eventsParams = paginationParams(pagination.events || {}, 50)
  const workResponse = await api.fetchWorkDetail(workId)
  const rawWork = unwrap(workResponse, {})
  const work = normalizeWork(rawWork)
  const flowAccess = work.flow_access
  const canViewMainFlow = !flowAccess.contractAvailable
    || flowAccess.capabilities?.can_view_main_flow?.allowed === true
  const visibleSurfaces = new Set(flowAccess.visibleSurfaces || ['flow'])
  const canLoadSurface = surface => !flowAccess.contractAvailable || visibleSurfaces.has(surface)
  const flowResponse = canViewMainFlow
    ? await api.fetchWorkFlow(workId)
    : { data: { work: {}, items: [], edges: [] } }
  const flow = unwrap(flowResponse, {})
  const rawFlowItems = Array.isArray(flow.items) ? flow.items : []
  const needsRevisionLayout = rawFlowItems.some(item => !storedWorkItemLayout(item))
  const needsPublishedRuntimeGraph = !flowAccess.contractAvailable
    && canViewMainFlow
    && rawWork.runtime?.enabled === true
  const revisionRequest = (needsRevisionLayout || needsPublishedRuntimeGraph) && rawWork.template_revision_id
    ? api.fetchTemplateRevisionDetail(rawWork.template_revision_id)
      .then(data => ({ data }))
      .catch(error => ({ error }))
    : Promise.resolve({ data: null })
  const [runsResult, outputsResult, eventsResult, revisionResult] = await Promise.all([
    (canLoadSurface('runs') ? api.fetchWorkRuns(workId, runsParams) : Promise.resolve({ data: [], meta: {} }))
      .then(data => ({ data }))
      .catch(error => ({ error })),
    (canLoadSurface('outputs') ? api.fetchWorkOutputs(workId, outputsParams) : Promise.resolve({ data: [], meta: {} }))
      .then(data => ({ data }))
      .catch(error => ({ error })),
    (options.includeEvents !== false && canLoadSurface('events') ? api.fetchWorkEvents(workId, eventsParams) : Promise.resolve({ data: [], meta: {} }))
      .then(data => ({ data }))
      .catch(error => ({ error })),
    revisionRequest,
  ])
  const permissions = normalizeWorkPermissions(rawWork, flow)
  work.permissions = permissions
  const apiOutputs = outputsResult.error ? [] : asArray(outputsResult.data).map(normalizeOutput)
  const runs = runsResult.error ? [] : applyOfficialRunStatus(
    asArray(runsResult.data).map(normalizeRun),
    apiOutputs,
    work.official_output_id
  )
  const outputById = new Map()
  const pushOutput = output => {
    if (!output) return
    const key = String(output.id || `${output.run_id || 'run'}-${output.file_id || output.display_name || outputById.size}`)
    if (!outputById.has(key)) outputById.set(key, output)
  }
  apiOutputs.forEach(pushOutput)
  runs.flatMap(outputsFromRun).forEach(pushOutput)
  const aggregatedOutputs = Array.from(outputById.values())
  const flows = (flow.edges || flow.flows || []).map(edge => ({ ...edge }))
  const templateRevision = revisionResult.error
    ? null
    : unwrap(revisionResult.data, {})
  const revisionLayouts = revisionResult.error
    ? new Map()
    : templateRevisionWorkItemLayouts(templateRevision)
  const items = rawFlowItems.map((item, index) => {
    const layout = storedWorkItemLayout(item)
      || revisionLayouts.get(String(item.client_key || item.key))
    return normalizeWorkItem(layout ? { ...item, layout } : item, work, index)
  })
  const itemByClientKey = new Map()
  const itemById = new Map()
  items.forEach(item => {
    const clientKeyReferences = [item.client_key, item.key]
    clientKeyReferences.forEach(reference => {
      if (reference !== undefined && reference !== null && reference !== '') {
        itemByClientKey.set(String(reference), item)
      }
    })
    const itemIdReferences = [item.id, item.local_id]
    itemIdReferences.forEach(reference => {
      if (reference !== undefined && reference !== null && reference !== '') {
        itemById.set(String(reference), item)
      }
    })
  })
  const edgeItem = (canonicalReferences, itemIdReference) => {
    const canonicalReference = canonicalReferences.find(value => value !== undefined && value !== null && value !== '')
    if (canonicalReference !== undefined) {
      const key = String(canonicalReference)
      return itemByClientKey.get(key) || itemById.get(key)
    }
    return itemById.get(String(itemIdReference))
  }
  flows.forEach(edge => {
    const from = edgeItem([edge.from_node_key, edge.from_client_key], edge.from_item_id)
    const to = edgeItem([edge.to_node_key, edge.to_client_key], edge.to_item_id)
    if (from && to) {
      from.outgoing.push({ id: to.id, key: to.key, client_key: to.client_key, name: to.name, status: to.status, item_type: to.item_type })
      to.incoming.push({
        id: from.id,
        key: from.key,
        client_key: from.client_key,
        name: from.name,
        status: from.status,
        item_type: from.item_type,
        input_versions: from.input_versions || [],
        attempts: from.attempts || [],
      })
    }
  })
  items.forEach(item => {
    item.runs = runs.filter(run => String(run.work_item_id) === String(item.id))
    const runIds = new Set(item.runs.map(run => String(run.id)))
    const directOutputs = aggregatedOutputs.filter(output => (
      String(output.source_work_item_id || output.work_item_id) === String(item.id) ||
      runIds.has(String(output.run_id))
    ))
    const existingOutputIds = new Set(directOutputs.map(output => String(output.id)))
    const fallbackOutputs = item.runs
      .filter(run => !(run.output_ids || []).some(outputId => existingOutputIds.has(String(outputId))))
      .flatMap(outputsFromRun)
    const outputsForItem = [...directOutputs, ...fallbackOutputs]
    const additionalRpaOutputs = item.runs.flatMap(run => (
      additionalOutputsFromRpaRetPks(
        run,
        outputsForItem.filter(output => String(output.run_id) === String(run.id))
      )
    ))
    item.outputs = [...outputsForItem, ...additionalRpaOutputs]
  })
  if (!work.assignee_names.length) {
    work.assignee_names = Array.from(new Set(items.flatMap(item => (item.assignees || []).map(row => row.name).filter(Boolean))))
  }
  return {
    work,
    items,
    flows,
    flow_items: publishedWorkFlowItems(items, templateRevision),
    flow_flows: publishedWorkFlowEdges(flows, templateRevision),
    runs,
    outputs: apiOutputs,
    events: eventsResult.error ? [] : asArray(eventsResult.data).map(normalizeWorkEvent),
    final_snapshots: [],
    comments: [],
    restricted: {
      runs: runsResult.error ? apiErrorMessage(runsResult.error) : '',
      outputs: outputsResult.error ? apiErrorMessage(outputsResult.error) : '',
      events: eventsResult.error ? apiErrorMessage(eventsResult.error) : '',
    },
    restrictedErrors: {
      runs: runsResult.error || null,
      outputs: outputsResult.error || null,
      events: eventsResult.error || null,
    },
    pagination: {
      runs: normalizePaginationMeta(metaOf(runsResult.data) || {}, runsParams),
      outputs: normalizePaginationMeta(metaOf(outputsResult.data) || {}, outputsParams),
      events: normalizePaginationMeta(metaOf(eventsResult.data) || {}, eventsParams),
    },
    permissions,
  }
}

export const workManagementService = {
  useApi: true,
  api,
  apiErrorMessage,
  apiErrorCode,
  isWorkTreeValidationError,
  isObjectPermissionError,
  isVersionConflictError,
  isAsyncRpaRun,
  isFailedWorkRun,
  isReviewableWorkRun,
  workRunDisplayOutputs,
  workRunFailure,
  workRunFailureMessage,
  startAsyncRpaPanelTracking,
  isRpaAsyncPlaceholderOutput,
  hasPendingRpaAsyncOutput,

  getWorkMembers() {
    return cachedAssigneeCandidates
  },

  async loadProjectAssigneeCandidates(projectId, options = {}) {
    return (await this.loadProjectAssignmentOptions(projectId, options)).members
  },

  async loadAssignmentConditionCandidates(projectId) {
    return unwrap(await api.fetchProjectAssigneeCandidates(projectId), {})
  },

  async previewProjectAssignment(projectId, assignment, classificationValue) {
    return unwrap(await api.previewProjectAssignment(projectId, {
      assignment,
      classification_value: classificationValue,
    }), {})
  },

  async loadProjectAssignmentOptions(projectId, options = {}) {
    if (!projectId) {
      cachedAssigneeProjectId = null
      cachedAssigneeCandidates = []
      cachedAssigneeOrganizations = []
      return { members: cachedAssigneeCandidates, organizations: cachedAssigneeOrganizations }
    }
    if (!options.force && String(cachedAssigneeProjectId) === String(projectId) && cachedAssigneeCandidates.length) {
      return { members: cachedAssigneeCandidates, organizations: cachedAssigneeOrganizations }
    }
    const response = await api.fetchProjectAssigneeCandidates(projectId)
    const data = unwrap(response, {})
    const members = (data.members || [])
      .map(normalizeAssigneeCandidate)
      .filter(Boolean)
    const organizations = (data.organizations || [])
      .map(normalizeAssigneeOrganization)
      .filter(Boolean)
    cachedAssigneeProjectId = projectId
    cachedAssigneeCandidates = members
    cachedAssigneeOrganizations = organizations
    return { members, organizations }
  },

  async getFilePreviewData(file, limit = 50) {
    const fileId = file?.file_id || file?.multi_table_data_id || file?.data_id || file?.id || file
    if (!fileId) throw new Error('FILE_ID_REQUIRED')
    const response = await api.fetchFilePreviewData(fileId, limit)
    return normalizePreviewData(
      {
        ...file,
        id: fileId,
        name: file?.name || file?.original_filename || file?.display_name,
      },
      unwrap(response, {})
    )
  },

  getWorkGroups() {
    return cachedAssigneeOrganizations
  },

  async getWorkTemplates(params = {}) {
    return (await this.getWorkTemplatesWithMeta(params)).data
  },

  async getWorkTemplatesWithMeta(params = {}) {
    const safeParams = paginationParams(params)
    const response = await api.fetchWorkTemplates(safeParams)
    return normalizeList(response, normalizeTemplate, safeParams)
  },

  getAllWorkTemplates(params = {}) {
    return collectAllPages(pageParams => this.getWorkTemplatesWithMeta(pageParams), params)
  },

  async getWorkTemplate(id) {
    const response = await api.fetchWorkTemplate(id)
    return normalizeTemplate(unwrap(response, null))
  },

  async createWorkTemplate(payload) {
    validateTemplateGraphRpaOutputMappings(payload)
    let response = await api.createWorkTemplate(templateBasePayload(payload))
    let template = normalizeTemplateResponse(response)
    if (shouldWriteTemplateGraph(payload)) {
      const graph = templateGraphForWrite(payload, template.tree_version)
      response = await api.saveWorkTemplateGraph(template.id, buildTemplateGraphPayload({
        graph,
        expectedTreeVersion: template.tree_version,
      }))
      template = normalizeTemplateResponse(response, template)
    }
    return { data: template }
  },

  async updateWorkTemplate(id, payload) {
    validateTemplateGraphRpaOutputMappings(payload)
    let response = await api.updateWorkTemplate(id, {
      ...buildWorkTemplateUpdatePayload(payload),
      expected_tree_version: payload.expected_tree_version || payload.tree_version,
    })
    let template = normalizeTemplateResponse(response, normalizeTemplate(payload))
    if (shouldWriteTemplateGraph(payload)) {
      const graph = templateGraphForWrite(payload, template.tree_version)
      response = await api.saveWorkTemplateGraph(id, buildTemplateGraphPayload({
        graph,
        expectedTreeVersion: template.tree_version,
      }))
      template = normalizeTemplateResponse(response, template)
    }
    return { data: template }
  },

  async saveWorkTemplateGraph(id, payload) {
    validateTemplateGraphRpaOutputMappings(payload)
    const graph = templateGraphForWrite(payload, payload.expected_tree_version || payload.tree_version)
    const response = await api.saveWorkTemplateGraph(id, buildTemplateGraphPayload({
      graph,
      expectedTreeVersion: payload.expected_tree_version || payload.tree_version || graph.tree_version,
    }))
    return { data: normalizeTemplateResponse(response, normalizeTemplate(payload)) }
  },

  async cloneWorkTemplate(id, payload = {}) {
    const source = await this.getWorkTemplate(id)
    const response = await api.cloneWorkTemplate(id, {
      name: payload.name || `${source.name} 복제본`,
      ...(payload.target_project_id ? { target_project_id: payload.target_project_id } : {}),
      rpa_copy_mode: payload.rpa_copy_mode || 'reference',
      reference_mapping: payload.reference_mapping || {},
      change_summary: payload.change_summary || '',
    })
    return { data: normalizeTemplate(unwrap(response, null)) }
  },

  async publishWorkTemplate(id, payload) {
    const response = await api.publishWorkTemplate(id, payload)
    return { data: normalizeWork(unwrap(response, null)) }
  },

  async getWorkTemplatePermissions(id) {
    const response = await api.fetchTemplatePermissions(id)
    return normalizeTemplatePermission(unwrap(response, {}))
  },

  async saveWorkTemplatePermissions(id, payload = {}) {
    const current = normalizeTemplatePermission(payload)
    const response = await api.updateTemplatePermissions(id, {
      permission_version: current.permission_version,
      permissions: current.permissions || {},
      change_summary: payload.change_summary || '',
    })
    return normalizeTemplatePermission(unwrap(response, {}))
  },

  async getWorkTemplateRevisions(id, params = {}) {
    return (await this.getWorkTemplateRevisionsWithMeta(id, params)).data
  },

  async getWorkTemplateRevisionsWithMeta(id, params = {}) {
    const safeParams = paginationParams(params)
    const response = await api.fetchTemplateRevisions(id, safeParams)
    return normalizeList(response, row => row, safeParams)
  },

  async getWorkTemplateRevision(id) {
    const response = await api.fetchTemplateRevisionDetail(id)
    return unwrap(response, null)
  },

  async getWorkRevisions(id, params = {}) {
    return (await this.getWorkRevisionsWithMeta(id, params)).data
  },

  async getWorkRevisionsWithMeta(id, params = {}) {
    const safeParams = paginationParams(params)
    const response = await api.fetchWorkRevisions(id, safeParams)
    return normalizeList(response, row => row, safeParams)
  },

  async getWorkRevision(id) {
    const response = await api.fetchWorkRevisionDetail(id)
    return unwrap(response, null)
  },

  async getWorks(params = {}) {
    return (await this.getWorksWithMeta(params)).data
  },

  async getWorksWithMeta(params = {}) {
    const safeParams = paginationParams(params)
    const response = await api.fetchWorks(safeParams)
    return normalizeList(response, normalizeWork, safeParams)
  },

  getAllWorks(params = {}) {
    return collectAllPages(pageParams => this.getWorksWithMeta(pageParams), params)
  },

  async getWorkDetail(id, pagination = {}, options = {}) {
    return buildWorkDetail(id, pagination, options)
  },

  async getWorkSummary(id) {
    const response = await api.fetchWorkDetail(id)
    return normalizeWork(unwrap(response, {}))
  },

  async cleanupWork(id, payload) {
    const response = await api.deleteWork(id, payload)
    return { data: unwrap(response, {}) }
  },

  async getWorkFlowState(id) {
    const response = await api.fetchWorkFlow(id)
    const flow = unwrap(response, {})
    return {
      work: flow.work || {},
      items: Array.isArray(flow.items) ? flow.items : [],
    }
  },

  async getWorkRouteContext(id) {
    const response = await api.fetchWorkDetail(id)
    const work = normalizeWork(unwrap(response, {}))
    return {
      workId: work.id || Number(id),
      projectId: work.project_id || null,
    }
  },

  async getWorkFlow(id, pagination = {}) {
    return buildWorkDetail(id, pagination)
  },

  updateWorkTree(id, payload = {}) {
    const hasEdges = Object.prototype.hasOwnProperty.call(payload, 'flows')
      || Object.prototype.hasOwnProperty.call(payload, 'edges')
    const editableGraph = editableWorkTreeGraph(
      payload.items || [],
      payload.flows || payload.edges || [],
    )
    return api.updateWorkTree(id, {
      expected_tree_version: payload.expected_tree_version,
      mode: payload.mode || 'fork_new_tree',
      reason: payload.reason || '',
      items: workItemsPayload(editableGraph.items),
      ...(hasEdges ? { edges: workEdgesPayload(editableGraph.flows, editableGraph.items) } : {}),
    })
  },

  async getMyWorkItems(params = {}) {
    return (await this.getMyWorkItemsWithMeta(params)).data
  },

  async getMyWorkItemsWithMeta(params = {}) {
    const safeParams = paginationParams(params)
    const response = await api.fetchMyWorkItems(safeParams)
    return normalizeList(response, (item, index) => normalizeWorkItem(item, null, index), safeParams)
  },

  getAllMyWorkItems(params = {}) {
    return collectAllPages(pageParams => this.getMyWorkItemsWithMeta(pageParams), params)
  },

  async getMyAssignmentHistoryWithMeta(params = {}) {
    const safeParams = assignmentHistoryParams(params)
    const response = await api.fetchMyAssignmentHistory(safeParams)
    if (response?.error) throw response
    return normalizeList(response, occurrence => occurrence, safeParams)
  },

  async getMyAssignmentHistoryDetail(occurrenceId) {
    const response = await api.fetchMyAssignmentHistoryDetail(occurrenceId)
    if (response?.error) throw response
    return unwrap(response, null)
  },

  async getWorkItemAssignmentHistoryWithMeta(workItemId, params = {}) {
    const safeParams = assignmentHistoryParams(params, { itemScoped: true })
    const response = await api.fetchWorkItemAssignmentHistory(workItemId, safeParams)
    if (response?.error) throw response
    return normalizeList(response, occurrence => occurrence, safeParams)
  },

  async getWorkItem(id, params = {}) {
    const safeParams = assignmentHistoryParams(params, { itemScoped: true })
    const itemResponse = await api.fetchWorkItem(id, safeParams)
    if (itemResponse?.error) throw itemResponse
    const baseItem = unwrap(itemResponse, null)
    if (!baseItem) return null
    return normalizeWorkItemAggregate(baseItem)
  },

  async getWorkNotifications(params = {}) {
    return (await this.getWorkNotificationsWithMeta(params)).data
  },

  async getWorkNotificationsWithMeta(params = {}) {
    const safeParams = paginationParams(params)
    const response = await api.fetchNotifications(safeParams)
    const seen = new Set()
    const result = normalizeList(response, normalizeNotification, safeParams)
    result.data = result.data.filter(notification => {
      const key = notification.dedupe_key || notification.id
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    return result
  },

  getAllWorkNotifications(params = {}) {
    return collectAllPages(
      pageParams => this.getWorkNotificationsWithMeta(pageParams),
      params,
      notification => notification?.dedupe_key || notification?.id
    )
  },

  async getNotificationUnreadCount(params = {}) {
    const response = await api.fetchUnreadNotificationCount(params)
    const data = unwrap(response, {})
    return Number(data.unread_count || 0)
  },

  async getNotificationDigest(params = {}) {
    const response = await api.fetchNotificationDigest(params)
    return asArray(response).map(normalizeNotification)
  },

  async getRpaItems(projectPk, params = {}) {
    const response = await api.fetchRpaItems(projectPk, {
      order: params.order || '-updated_at',
      page: params.page || 1,
      limit: params.limit || 9999,
      version_major: params.version_major || 2,
    })
    const list = asArray(response).filter(Boolean)
    if (!params.search) return list
    return list.filter(item => String(item.name || '').includes(params.search))
  },

  async markNotificationRead(id) {
    return api.markNotificationRead(id)
  },

  async markAllNotificationsRead(projectId) {
    return api.markAllNotificationsRead(projectId)
  },

  setWorkItemStatus(id, status, payload = {}) {
    return api.updateWorkItemStatus(id, { status, ...payload })
  },

  async withdrawWorkItemInput(id, payload) {
    const response = await api.withdrawWorkItemInput(id, payload)
    if (response?.error) throw response
    return unwrap(response, null)
  },

  updateWorkItemAssignees(id, payload = {}) {
    const assignees = Array.isArray(payload.assignees) ? normalizeAssignmentSources(payload.assignees) : buildWorkItemAssignments(
      (payload.assignee_ids || [])[0],
      payload.watcher_ids || []
    )
    return api.updateWorkItemAssignees(id, { ...payload, assignees })
  },

  setOfficialOutput(outputId, payload = {}) {
    return api.setOfficialWorkOutput(outputId, payload)
  },

  setOfficialRun(runId, payload = {}) {
    return api.setOfficialWorkRun(runId, payload)
  },

  async getWorkOutput(outputId) {
    if (!outputId) return null
    const response = await api.fetchWorkOutput(outputId)
    return normalizeOutput(unwrap(response, null))
  },

  async getWorkRuns(workId, params = {}) {
    if (!workId) return []
    return (await this.getWorkRunsWithMeta(workId, params)).data
  },

  async getWorkRunsWithMeta(workId, params = {}) {
    if (!workId) return { data: [], meta: normalizePaginationMeta({}, params) }
    const safeParams = paginationParams(params)
    const response = await api.fetchWorkRuns(workId, safeParams)
    return normalizeList(response, normalizeRun, safeParams)
  },

  getAllWorkRuns(workId, params = {}) {
    return collectAllPages(pageParams => this.getWorkRunsWithMeta(workId, pageParams), params)
  },

  async getWorkOutputsWithMeta(workId, params = {}) {
    if (!workId) return { data: [], meta: normalizePaginationMeta({}, params) }
    const safeParams = paginationParams(params)
    const response = await api.fetchWorkOutputs(workId, safeParams)
    return normalizeList(response, normalizeOutput, safeParams)
  },

  async getWorkEventsWithMeta(workId, params = {}) {
    if (!workId) return { data: [], meta: normalizePaginationMeta({}, params) }
    const safeParams = paginationParams(params)
    const response = await api.fetchWorkEvents(workId, safeParams)
    return normalizeWorkEventList(response, safeParams)
  },

  async getProjectWorkEventsWithMeta(projectId, params = {}) {
    if (!projectId) {
      return {
        data: [],
        meta: normalizePaginationMeta({}, params),
        filterOptions: normalizeWorkEventListPayload({}).filterOptions,
      }
    }
    const safeParams = { ...paginationParams(params), project_id: projectId }
    const response = await api.fetchProjectWorkEvents(safeParams)
    return normalizeWorkEventList(response, safeParams)
  },

  rollbackOutput(workId, outputId, payload = {}) {
    return api.rollbackWorkOutput(outputId, payload)
  },

  rollbackRun(runId, payload = {}) {
    return api.rollbackWorkRun(runId, payload)
  },

  createWorkItemInput(id, payload) {
    return api.createWorkItemInput(id, payload)
  },

  async syncWorkItemFormSchema(id, payload) {
    const result = await api.syncWorkItemFormSchema(id, payload)
    if (result?.error) throw result
    return unwrap(result, null)
  },

  async submitWorkItemInputDraft(id, payload) {
    const result = await api.createWorkItemInput(id, payload)
    if (result?.error) throw result
    return unwrap(result, null)
  },

  async getWorkItemInputDraft(id, params = {}) {
    const result = await api.fetchWorkItemInputDraft(id, params)
    if (result?.error) throw result
    return unwrap(result, null)
  },

  async saveWorkItemInputDraft(id, payload) {
    const result = await api.saveWorkItemInputDraft(id, payload)
    if (result?.error) throw result
    return unwrap(result, null)
  },

  async deleteWorkItemInputDraft(id, payload) {
    const result = await api.deleteWorkItemInputDraft(id, payload)
    if (result?.error) throw result
    return unwrap(result, null)
  },

  runWorkFormTextEngine(payload) {
    return api.runWorkFormTextEngine(payload)
  },

  addWorkFormSourceRow(payload) {
    return api.addWorkFormSourceRow(payload)
  },

  createWorkItemManualInput(id, payload = {}) {
    return api.createWorkItemManualInput(id, payload)
  },

  updateWorkExtraFields(id, payload = {}) {
    return api.updateWorkExtraFields(id, payload)
  },

  updateWorkItemExtraFields(id, payload = {}) {
    return api.updateWorkItemExtraFields(id, payload)
  },

  createWorkItemComment(id, payload = {}) {
    return api.createWorkItemComment(id, payload)
  },

  rejectWorkItem(id, payload = {}) {
    return api.rejectWorkItem(id, payload)
  },

  async getRpaInputDefinition(id, activities = []) {
    if (!id) {
      return {
        id,
        input: { schema_version: '2.1', use_input_fields: false, fields: [] },
        source: 'empty',
      }
    }
    const response = await api.fetchRpaItem(id)
    const definition = normalizeRpaInputDefinition(unwrap(response, {}))
    if (Array.isArray(activities) && activities.length) {
      definition.input = {
        schema_version: '2.1',
        use_input_fields: true,
        fields: activities.map(normalizeRpaInputActivity),
      }
      definition.source = 'work_management_rpa_input_activities'
    }
    return definition
  },

  runWorkItemRpa(id, payload = {}) {
    return api.runWorkItemRpa(id, payload).then(run => normalizeRun(unwrap(run, run)))
  },

  reviewWorkRun(runId, payload = {}) {
    return api.reviewWorkRun(runId, payload)
  },

  async getWorkRun(runId) {
    if (!runId) return null
    const response = await api.fetchWorkRun(runId)
    return normalizeRun(unwrap(response, null))
  },

  async getWorkRunInputSnapshots(runId) {
    if (!runId) return []
    const response = await api.fetchWorkRunInputSnapshots(runId)
    return asArray(response)
  },

  async getExternalEvents(params = {}) {
    const safeParams = paginationParams(params)
    const response = await api.fetchExternalEvents(safeParams)
    return normalizeList(response, normalizeExternalEvent, safeParams)
  },

  async getExternalEvent(id, params = {}) {
    if (!id) return null
    const response = await api.fetchExternalEventDetail(id, params)
    return normalizeExternalEvent(unwrap(response, null))
  },

  receiveExternalWorkItemEvent(externalKey, payload = {}) {
    return api.receiveExternalWorkItemEvent(externalKey, payload)
  },

  getStatusText(status) {
    return STATUS_TEXT[status] || status || '-'
  },

  getItemTypeText(type) {
    return ITEM_TYPE_TEXT[type] || type || '-'
  },

  formatWorkDate(value) {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  },
}
